import React, { useContext, useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import GridToolbarNoExport from "../../common/GridToolbarNoExport";
import { useAuth } from "../../../config/AuthContext";
import { SchoolContext } from "../../../config/SchoolContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../config/firebase";
import useData from "../hooks/useData";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faChartLine,
  faChartPie,
  faChartArea,
  faUsers,
  faPenToSquare,
  faCheckCircle,
  faGraduationCap,
  faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import DistrictAccountInfo from "./DistrictAccountInfo";
import DistrictSchoolUsers from "./DistrictSchoolUsers";
import DistrictSchoolData from "./DistrictSchoolData";
import DistrictEligibility from "./DistrictEligibility";
import DistrictGradeReport from "./DistrictGradeReport";
import DistrictRegReport from "./DistrictRegReport";
import DistrictSchoolReports from "./DistrictSchoolReports";
import DistrictSchoolForms from "./DistrictSchoolForms";

const DistrictSchoolsList = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { setSchoolId } = useContext(SchoolContext);
  const [district, setDistrict] = useState("");
  const [accountInfoOpen, setAccountInfoOpen] = useState(false);
  const [schoolUsersOpen, setSchoolUsersOpen] = useState(false);
  const [schoolDataOpen, setSchoolDataOpen] = useState(false);
  const [eligibilityOpen, setEligibilityOpen] = useState(false);
  const [gradeReportOpen, setGradeReportOpen] = useState(false);
  const [regReportOpen, setRegReportOpen] = useState(false);
  const [schoolReportsOpen, setSchoolReportsOpen] = useState(false);
  const [schoolFormsOpen, setSchoolFormsOpen] = useState(false);
  const [selectedSchoolName, setSelectedSchoolName] = useState("");
  const [selectedSchoolId, setSelectedSchoolId] = useState("");

  useEffect(() => {
    const fetchUserDistrict = async () => {
      if (currentUser) {
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setDistrict(userData.district);
        } else {
          console.error("No such document!");
        }
      }
    };

    fetchUserDistrict();
  }, [currentUser]);

  const { data: schools, loading } = useData(district);

  const handleAccountInfoOpen = (schoolName) => {
    setSelectedSchoolName(schoolName);
    setAccountInfoOpen(true);
  };

  const handleAccountInfoClose = () => {
    setAccountInfoOpen(false);
  };

  const handleSchoolUsersOpen = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setSchoolUsersOpen(true);
  };

  const handleSchoolUsersClose = () => {
    setSchoolUsersOpen(false);
  };

  const handleSchoolDataOpen = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setSchoolDataOpen(true);
  };

  const handleSchoolDataClose = () => {
    setSchoolDataOpen(false);
  };

  const handleEligibilityOpen = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setEligibilityOpen(true);
  };

  const handleEligibilityClose = () => {
    setEligibilityOpen(false);
  };

  const handleGradeReportOpen = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setGradeReportOpen(true);
  };

  const handleGradeReportClose = () => {
    setGradeReportOpen(false);
  };

  const handleRegReportOpen = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setRegReportOpen(true);
  };

  const handleRegReportClose = () => {
    setRegReportOpen(false);
  };

  const handleSchoolReportsOpen = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setSchoolReportsOpen(true);
  };

  const handleSchoolReportsClose = () => {
    setSchoolReportsOpen(false);
  };

  const handleSchoolFormsOpen = (schoolId) => {
    setSelectedSchoolId(schoolId);
    setSchoolFormsOpen(true);
  };

  const handleSchoolFormsClose = () => {
    setSchoolFormsOpen(false);
  };

  const handleCoursePlanningOpen = (schoolId) => {
    // Restricted Courses / Course Planning are scoped to a single school via
    // SchoolContext, same as when a school user is logged in directly.
    setSchoolId(schoolId);
    navigate("/CcpSchoolRestrictedCourses");
  };

  const columns = [
    { field: "school_name", headerName: "School Name", flex: 1 },
    { field: "district", headerName: "District", flex: 1 },
    {
      field: "accountInfo",
      headerName: "Account Info",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faUser} />}
          onClick={() => handleAccountInfoOpen(params.row.school_name)}
        >
          View
        </Button>
      ),
    },
    {
      field: "schoolReports",
      headerName: "School Reports",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faChartLine} />}
          onClick={() => handleSchoolReportsOpen(params.row.school_id)}
        >
          View
        </Button>
      ),
    },
    {
      field: "schoolData",
      headerName: "School Data",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faChartPie} />}
          onClick={() => handleSchoolDataOpen(params.row.school_id)}
        >
          View
        </Button>
      ),
    },
    {
      field: "schoolUsers",
      headerName: "School Users",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faUsers} />}
          onClick={() => handleSchoolUsersOpen(params.row.school_id)}
        >
          View
        </Button>
      ),
    },
    {
      field: "eligibility",
      headerName: "Eligibility",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faCheckCircle} />}
          onClick={() => handleEligibilityOpen(params.row.school_id)}
        >
          View
        </Button>
      ),
    },
    {
      field: "gradeReport",
      headerName: "Grade Report",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faGraduationCap} />}
          onClick={() => handleGradeReportOpen(params.row.school_id)}
        >
          View
        </Button>
      ),
    },
    {
      field: "regReport",
      headerName: "Registration Report",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faClipboardList} />}
          onClick={() => handleRegReportOpen(params.row.school_id)}
        >
          View
        </Button>
      ),
    },
    {
      field: "schoolForms",
      headerName: "School Forms",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
          onClick={() => handleSchoolFormsOpen(params.row.school_id)}
        >
          View
        </Button>
      ),
    },
    {
      field: "coursePlanning",
      headerName: "Course Planning",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faChartArea} />}
          onClick={() => handleCoursePlanningOpen(params.row.school_id)}
        >
          View
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m={2}>
      <Typography variant="h4" gutterBottom>
        Schools in {district}
      </Typography>
      <DataGrid
        rows={schools}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        disableSelectionOnClick
        autoHeight
        slots={{ toolbar: GridToolbarNoExport }}
      />
      <DistrictAccountInfo
        open={accountInfoOpen}
        onClose={handleAccountInfoClose}
        schoolName={selectedSchoolName}
      />
      <DistrictSchoolUsers
        open={schoolUsersOpen}
        onClose={handleSchoolUsersClose}
        schoolId={selectedSchoolId}
      />
      <DistrictSchoolData
        open={schoolDataOpen}
        onClose={handleSchoolDataClose}
        schoolId={selectedSchoolId}
      />
      <DistrictEligibility
        open={eligibilityOpen}
        onClose={handleEligibilityClose}
        schoolId={selectedSchoolId}
      />
      <DistrictGradeReport
        open={gradeReportOpen}
        onClose={handleGradeReportClose}
        schoolId={selectedSchoolId}
      />
      <DistrictRegReport
        open={regReportOpen}
        onClose={handleRegReportClose}
        schoolId={selectedSchoolId}
      />
      <DistrictSchoolReports
        open={schoolReportsOpen}
        onClose={handleSchoolReportsClose}
        schoolId={selectedSchoolId}
      />
      <DistrictSchoolForms
        open={schoolFormsOpen}
        onClose={handleSchoolFormsClose}
        schoolId={selectedSchoolId}
      />
    </Box>
  );
};

export default DistrictSchoolsList;
